import { Component } from '@theme/component';

const DRAG_THRESHOLD = 4;
const DISMISS_DISTANCE_RATIO = 0.3;
const DISMISS_DISTANCE_FLOOR = 60;
const DISMISS_VELOCITY = 0.5;

/**
 * A drag handle for bottom-sheet style dialogs. Place inside any
 * `<dialog>` that should be dismissible by dragging downward. On
 * release past the dismiss threshold (30% of sheet height, or 60px +
 * downward velocity), it walks up the DOM and calls `closeDialog()` /
 * `close()` on the nearest containing component. Otherwise the sheet
 * springs back to rest.
 *
 * Reusable across the search modal, cart drawer, account drawer, etc.
 * The consumer is responsible for visibility (hide on desktop with a
 * media query — the gesture only makes sense on touch).
 *
 * @extends Component<{}>
 */
class BottomSheetHandle extends Component {
  /** @type {HTMLDialogElement | null} */ #sheet = null;
  /** @type {AbortController | null} */ #abort = null;
  #startY = 0;
  #lastY = 0;
  #lastT = 0;
  #velocity = 0;
  #isDragging = false;
  #savedTransition = '';
  #savedAnimation = '';

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerdown', this.#onDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  #onDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const sheet = /** @type {HTMLDialogElement | null} */ (this.closest('dialog'));
    if (!sheet) return;

    this.#sheet = sheet;
    this.#startY = this.#lastY = event.clientY;
    this.#lastT = performance.now();
    this.#velocity = 0;
    this.#isDragging = false;

    // Document-level listeners survive the dialog moving under the
    // finger and the pointer leaving the handle's bounding box.
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };
    document.addEventListener('pointermove', this.#onMove, opts);
    document.addEventListener('pointerup', this.#onUp, opts);
    document.addEventListener('pointercancel', this.#onUp, opts);

    event.preventDefault();
  };

  #onMove = (event) => {
    if (!this.#sheet) return;

    const dy = event.clientY - this.#startY;

    if (!this.#isDragging) {
      if (Math.abs(dy) < DRAG_THRESHOLD) return;
      this.#isDragging = true;
      // Cancel any in-flight opening animation so our inline transforms
      // actually move the dialog. Save originals to restore on release.
      this.#savedTransition = this.#sheet.style.transition;
      this.#savedAnimation = this.#sheet.style.animation;
      this.#sheet.style.animation = 'none';
      this.#sheet.style.transition = 'none';
    }

    const now = performance.now();
    const dt = now - this.#lastT;
    if (dt > 0) {
      const instantaneous = (event.clientY - this.#lastY) / dt;
      this.#velocity = this.#velocity * 0.6 + instantaneous * 0.4;
    }
    this.#lastY = event.clientY;
    this.#lastT = now;

    // Free downward, rubber-band upward.
    const offset = dy > 0 ? dy : dy * 0.2;
    this.#sheet.style.transform = `translate3d(0, ${offset}px, 0)`;
  };

  #onUp = (event) => {
    if (!this.#sheet) return;
    this.#abort?.abort();
    this.#abort = null;

    const sheet = this.#sheet;
    this.#sheet = null;

    if (!this.#isDragging) return;

    const dy = event.clientY - this.#startY;
    const height = sheet.getBoundingClientRect().height || window.innerHeight;
    const shouldDismiss =
      dy > height * DISMISS_DISTANCE_RATIO ||
      (dy > DISMISS_DISTANCE_FLOOR && this.#velocity > DISMISS_VELOCITY);

    if (shouldDismiss) {
      // Hand the dragged offset to the closing keyframes via a custom
      // property so the dialog continues sliding from where the finger
      // left it instead of snapping back to rest for a frame.
      sheet.style.setProperty('--drawer-close-from', `${Math.max(0, dy)}px`);
      sheet.style.transform = '';
      sheet.style.transition = this.#savedTransition;
      sheet.style.animation = this.#savedAnimation;
      sheet.addEventListener(
        'animationend',
        () => sheet.style.removeProperty('--drawer-close-from'),
        { once: true }
      );
      this.#dismiss(sheet);
    } else {
      sheet.style.transition = 'transform 220ms cubic-bezier(0.32, 0.72, 0, 1)';
      sheet.style.transform = 'translate3d(0, 0, 0)';
      const cleanup = () => {
        sheet.style.transition = this.#savedTransition;
        sheet.style.animation = this.#savedAnimation;
        sheet.style.transform = '';
        sheet.removeEventListener('transitionend', cleanup);
      };
      sheet.addEventListener('transitionend', cleanup);
    }
  };

  /**
   * Walk up the DOM looking for a close method on a custom component.
   * Falls back to a bubbling event for consumers that wire their own.
   *
   * @param {HTMLDialogElement} sheet
   */
  #dismiss(sheet) {
    let el = sheet.parentElement;
    while (el) {
      const closeDialog = /** @type {any} */ (el).closeDialog;
      if (typeof closeDialog === 'function') {
        closeDialog.call(el);
        return;
      }
      const tag = el.tagName ?? '';
      const close = /** @type {any} */ (el).close;
      if (tag.endsWith('-COMPONENT') && typeof close === 'function') {
        close.call(el);
        return;
      }
      el = el.parentElement;
    }
    this.dispatchEvent(new CustomEvent('bottom-sheet:dismiss', { bubbles: true, composed: true }));
  }
}

if (!customElements.get('bottom-sheet-handle')) {
  customElements.define('bottom-sheet-handle', BottomSheetHandle);
}
